import React from "react";
import {
  WSCard,
  WSControl,
  WSFlexBox,
  WSInfoBox,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { IMeridioPlanOption } from "../../../../services/api/benefits/meridioPlanCatalog/types";
import styles from "./PlanCard.module.scss";

interface PlanCardProps {
  plan: IMeridioPlanOption;
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  controlType?: "radio" | "checkbox";
}

function getPriceRange(plan: IMeridioPlanOption): string {
  const premiums = [plan.io, plan.is, plan.ic, plan.if]
    .filter(Boolean)
    .map(tier => tier!.monthlyPremium);

  if (premiums.length === 0) {
    return "—";
  }

  const min = Math.min(...premiums);
  const max = Math.max(...premiums);

  if (min === max) {
    return `${formatMoney(min, Currency.USD)}/month`;
  }

  return `${formatMoney(min, Currency.USD)}/month – ${formatMoney(
    max,
    Currency.USD
  )}/month`;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  selectable,
  isSelected,
  onSelect,
  controlType = "radio"
}) => {
  const priceRange = getPriceRange(plan);

  const cardClassName = [
    styles.card,
    selectable ? styles.selectable : undefined,
    isSelected ? styles.selected : undefined
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <WSCard
      className={cardClassName}
      onClick={selectable ? onSelect : undefined}
    >
      <WSFlexBox.CenterY justify="space-between" mb="XL">
        <WSText.Heading3 color="gray600" weight="bold">
          {plan.name}
        </WSText.Heading3>
      </WSFlexBox.CenterY>
      <WSText.ParagraphSm weight="book" color="gray500">
        Cost:
      </WSText.ParagraphSm>
      <WSText.ParagraphSm weight="book" color="gray700">
        {priceRange}
      </WSText.ParagraphSm>
      <WSText.ParagraphSm weight="book" color="gray700">
        Based on who you cover
      </WSText.ParagraphSm>
      <WSText.ParagraphSm weight="book" color="gray500" mt="L">
        At a glance:
      </WSText.ParagraphSm>
      {plan.highlights && plan.highlights.length > 0 && (
        <WSInfoBox transparent>
          <ul>
            {plan.highlights.map((highlight, i) => (
              <li key={i}>{highlight}</li>
            ))}
          </ul>
        </WSInfoBox>
      )}
      {selectable && (
        <WSControl
          mt="M"
          type={controlType}
          value={!!isSelected}
          onChange={() => onSelect?.()}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          label={
            controlType === "radio" ? "Select this option" : "Add this option"
          }
        />
      )}
    </WSCard>
  );
};
